<template>
    <div>
        <div class="col-md-12" v-if="claims.tasks.create">
            <button type="button" @click="showCreate = true" class="btn btn-primary m-0" data-toggle="modal"
                    data-target="#createSigningModal">
                {{ $t("signing.new") }}
            </button>
        </div>

        <tabs :options="{ useUrlFragment: false, loadSavedTab: true }" cache-lifetime="10" class="m-0">
            <tab id="assignedToMeAttachments"
                :name="$t('signing.attachments')"
            >
                <div v-show="isSigning">
                    <progress-bar
                        v-model="progress"
                        min="0"
                        :max="progressMax"
                        :description="progressText"
                        class="my-2"
                    />
                </div>
                <div v-show="!isSigning">
                    <div class="d-flex flex-row justify-content-end">
                        <vs-radio
                            v-model="attachmentsFilter"
                            vs-name="attachmentsFilterRadio"
                            vs-value="all"
                        >
                            <span class="mr-1" v-cloak>{{ $t("all") }}</span>
                        </vs-radio>
                        <vs-radio
                            v-model="attachmentsFilter"
                            vs-name="attachmentsFilterRadio"
                            vs-value="signed"
                        >
                            <span class="mr-1" v-cloak>{{ $tc("signing.signed", 2) }}</span>
                        </vs-radio>
                        <vs-radio
                            v-model="attachmentsFilter"
                            vs-name="attachmentsFilterRadio"
                            vs-value="unsigned"
                        >
                            <span class="mr-1" v-cloak>{{ $tc("signing.unsigned", 2) }}</span>
                        </vs-radio>
                        <vs-radio
                            v-model="attachmentsFilter"
                            vs-name="attachmentsFilterRadio"
                            vs-value="forSigning"
                        >
                            <span class="mr-1" v-cloak>{{ $t("signing.forSigning") }}</span>
                        </vs-radio>
                    </div>
                    <div class="grid-wrapper">
                        <kendo-datasource
                            ref="datasource3"
                            :transport="assignedToMeAttachmentsTransportSettings"
                            :schema-model-fields="attachmentsSchemaModelFields"
                            :schema-model-id="'id'"
                            :schema-total="'total'"
                            :schema-data="'data'"
                            :page-size="10"
                            :server-paging="true"
                            :server-sorting="true"
                            :server-filtering="true"
                        />
                        <kendo-grid
                            ref="assignedToMeAttachmentsGrid"
                            :data-source-ref="'datasource3'"
                            :editable="gridOptions.editable"
                            :filterable="gridOptions.filterable"
                            :pageable="gridOptions.pageable"
                            :reorderable="gridOptions.reorderable"
                            :resizable="gridOptions.resizable"
                            :groupable="gridOptions.groupable"
                            :allow-copy="gridOptions.allowCopy"
                            :column-menu="gridOptions.columnMenu"
                            :sortable-mode="gridOptions.sortable.mode"
                            :sortable-allow-unsort="gridOptions.sortable.allowUnsort"
                            :sortable-show-indexes="gridOptions.sortable.showIndexes"
                            :selectable="gridOptions.selectable"
                            :scrollable="gridOptions.scrollable"
                            :dataBound="onAttachmentsDatabound"
                            :toolbar="attachmentsGridToolbar"
                            :excelExport="beforeExcelExport"
                        >
                        <kendo-grid-column
                            :selectable="true"
                            :width="50"
                            :lockable="true"
                            :locked="false"
                        />
                        <kendo-grid-column
                            :field="'id'"
                            :title="$t('signing.headers.id')"
                            :lockable="true"
                            :locked="false"
                            :hidden="true"
                        />
                        <kendo-grid-column
                            :width="400"
                            :field="'fileName'"
                            :title="$t('signing.filesGirdHeaders.fileName')"
                            :lockable="true"
                            :locked="false"
                        />
                        <!-- <kendo-grid-column
                            :field="'description'"
                            :title="$t('signing.filesGirdHeaders.fileDescription')"
                            :lockable="true"
                            :locked="false"
                        /> -->
                        <kendo-grid-column
                            :field="'signingCreatorShortFullName'"
                            :title="$t('signing.headers.creator')"
                            :lockable="true"
                            :locked="false"
                        />
                        <kendo-grid-column
                            :field="'assigneeShortFullName'"
                            :title="$t('signing.headers.signatories')"
                            :lockable="true"
                            :locked="false"
                        />
                        <kendo-grid-column
                            :field="'signingStatusName'"
                            :title="$t('signing.headers.status')"
                            :lockable="true"
                            :locked="false"
                        />
                        <!-- <kendo-grid-column
                            :field="'signingDeadline'"
                            :title="$t('signing.headers.deadline')"
                            :lockable="true"
                            :locked="false"
                            :format="customDateTimeFormat"
                        /> -->
                        <kendo-grid-column
                            :field="'signingStartDate'"
                            :title="$t('signing.headers.statDate')"
                            :lockable="true"
                            :locked="false"
                            :format="customDateTimeFormat"
                        />
                        <kendo-grid-column
                            :width="150"
                            :field="'attachmentIsSigned'"
                            :title="$t('signing.filesGirdHeaders.isSigned')"
                            :template="(e) => isSignedCellTemplate(e)"
                            :lockable="true"
                            :locked="false"
                        />
                        <kendo-grid-column
                            :field="'signingDate'"
                            :title="$t('signing.filesGirdHeaders.timestampUtc')"
                            :format="customDateTimeFormat"
                            :lockable="true"
                            :locked="false"
                        />
                        <kendo-grid-column
                            :field="'signatoryName'"
                            :title="$t('signing.filesGirdHeaders.signedBy')"
                            :lockable="true"
                            :locked="false"
                        />
                        <kendo-grid-column
                            :width="80"
                            :template="assignedToMeAttachmentsBtnsTemplate"
                            field="btnsColumn"
                            title=" "
                            :filterable="false"
                            :sortable="false"
                        />
                        </kendo-grid>
                    </div>
                </div>
            </tab>
            <tab id="assignedToMeProcedures"
                :name="$tc('signing.pageTitle', 2)"
            >
                <div>
                    <div class="d-flex flex-row justify-content-end">
                        <vs-radio
                            v-model="signingProceduresFilter"
                            vs-name="signingProceduresFilterRadio"
                            vs-value="all"
                        >
                            <span class="mr-1" v-cloak>{{ $t("all") }}</span>
                        </vs-radio>
                        <vs-radio
                            v-model="signingProceduresFilter"
                            vs-name="signingProceduresFilterRadio"
                            vs-value="completed"
                        >
                            <span class="mr-1" v-cloak>{{ $tc("signing.completed", 2) }}</span>
                        </vs-radio>
                        <vs-radio
                            v-model="signingProceduresFilter"
                            vs-name="signingProceduresFilterRadio"
                            vs-value="started"
                        >
                            <span class="mr-1" v-cloak>{{ $tc("signing.started", 2) }}</span>
                        </vs-radio>
                    </div>
                    <div class="grid-wrapper">
                        <kendo-datasource
                            ref="datasource1"
                            :transport="assignedToMeTransportSettings"
                            :schema-model-fields="schemaModelFields"
                            :schema-model-id="'id'"
                            :schema-total="'total'"
                            :schema-data="'data'"
                            :page-size="defaultGridRows"
                            :server-paging="true"
                            :server-sorting="true"
                            :server-filtering="true"
                        />
                        <kendo-grid
                            ref="assignedToMeGrid"
                            :data-source-ref="'datasource1'"
                            :editable="gridOptions.editable"
                            :filterable="gridOptions.filterable"
                            :pageable="gridOptions.pageable"
                            :reorderable="gridOptions.reorderable"
                            :resizable="gridOptions.resizable"
                            :groupable="gridOptions.groupable"
                            :allow-copy="gridOptions.allowCopy"
                            :column-menu="gridOptions.columnMenu"
                            :sortable-mode="gridOptions.sortable.mode"
                            :sortable-allow-unsort="gridOptions.sortable.allowUnsort"
                            :sortable-show-indexes="gridOptions.sortable.showIndexes"
                            :selectable="gridOptions.selectable"
                            :scrollable="gridOptions.scrollable"
                            :dataBound="onDatabound"
                        >
                            <kendo-grid-column
                                :width="80"
                                :template="assignToMeBtnsTemplate"
                                field="btnsColumn"
                                title=" "
                                :filterable="false"
                                :sorable="false"
                            />
                            <kendo-grid-column
                                :field="'id'"
                                :title="$t('signing.headers.id')"
                                :lockable="true"
                                :locked="false"
                                :hidden="true"
                            />
                            <kendo-grid-column
                                :field="'title'"
                                :title="$t('signing.headers.title')"
                                :lockable="true"
                                :locked="false"
                            />
                            <kendo-grid-column
                                :field="'statusName'"
                                :title="$t('signing.headers.status')"
                                :lockable="true"
                                :locked="false"
                            />
                            <kendo-grid-column
                                :field="'creatorName'"
                                :title="$t('signing.headers.creator')"
                                :lockable="true"
                                :locked="false"
                            />
                            <kendo-grid-column
                                :field="'signatoryName'"
                                :title="$t('signing.headers.signatories')"
                                :lockable="true"
                                :locked="false"
                            />
                            <!-- <kendo-grid-column
                                :field="'deadline'"
                                :title="$t('signing.headers.deadline')"
                                :lockable="true" :locked="false"
                                :format="customDateTimeFormat"
                            /> -->
                            <kendo-grid-column
                                field="startDate"
                                :title="$t('signing.headers.statDate')"
                                :lockable="true" :locked="false"
                                :format="customDateTimeFormat"
                            />
                        </kendo-grid>
                    </div>
                </div>
            </tab>
            <tab id="createdByMe"
                :name="$t('signing.createdByMe')"
            >
                <div>
                    <kendo-datasource
                        ref="datasource2"
                        :transport="createdByMeTransportSettings"
                        :schema-model-fields="schemaModelFields"
                        :schema-model-id="'id'"
                        :schema-total="'total'"
                        :schema-data="'data'"
                        :page-size="defaultGridRows"
                        :server-paging="true"
                        :server-sorting="true"
                        :server-filtering="true"
                    ></kendo-datasource>
                    <kendo-grid ref="createdByMeGrid"
                                :data-source-ref="'datasource2'"
                                :editable="gridOptions.editable"
                                :filterable="gridOptions.filterable"
                                :pageable="gridOptions.pageable"
                                :reorderable="gridOptions.reorderable"
                                :resizable="gridOptions.resizable"
                                :groupable="gridOptions.groupable"
                                :allow-copy="gridOptions.allowCopy"
                                :column-menu="gridOptions.columnMenu"
                                :sortable-mode="gridOptions.sortable.mode"
                                :sortable-allow-unsort="gridOptions.sortable.allowUnsort"
                                :sortable-show-indexes="gridOptions.sortable.showIndexes"
                                :selectable="gridOptions.selectable"
                                :scrollable="gridOptions.scrollable"
                                :dataBound="onCreatedByMeDatabound">
                        <kendo-grid-column
                            :width="100"
                            :template="btnsTemplate"
                            field="btnsColumn"
                            title=" "
                            :filterable="false"
                            :sirtable="false"
                        />
                        <kendo-grid-column
                            :field="'id'"
                            :title="$t('signing.headers.id')"
                            :lockable="true"
                            :locked="false"
                            :hidden="true"
                        />
                        <kendo-grid-column
                            :field="'title'"
                            :title="$t('signing.headers.title')"
                            :lockable="true"
                            :locked="false"
                        />
                        <kendo-grid-column
                            :field="'statusName'"
                            :title="$t('signing.headers.status')"
                            :lockable="true"
                            :locked="false"
                        />
                        <kendo-grid-column
                            :field="'creatorName'"
                            :title="$t('signing.headers.creator')"
                            :lockable="true"
                            :locked="false"
                        />
                        <kendo-grid-column
                            :field="'signatoryName'"
                            :title="$t('signing.headers.signatories')"
                            :lockable="true"
                            :locked="false"
                        />
                        <!-- <kendo-grid-column
                            :field="'deadline'"
                            :title="$t('signing.headers.deadline')"
                            :lockable="true"
                            :locked="false"
                            :format="customDateTimeFormat"
                        /> -->
                        <kendo-grid-column
                            field="startDate"
                            :title="$t('signing.headers.statDate')"
                            :lockable="true" :locked="false"
                            :format="customDateTimeFormat"
                        />
                    </kendo-grid>
                </div>
            </tab>
        </tabs>
        <CreateModal @onClose="onModalClose" :show="showCreate" @updated="refresh"></CreateModal>
        <EditModal v-model="activeItem" :show.sync="showEdit" @onClose="onModalClose" @updated="refresh"></EditModal>
        <DisplayModal v-model="activeItem" :show.sync="showDisplay" @onClose="onModalClose"
                      @updated="refresh"></DisplayModal>
        <SignModal v-model="activeItem"
            :show.sync="showSign"
            @onClose="onModalClose"
            @updated="refresh"
        />
        <CancelModal v-model="activeItemId" :show.sync="showCancel" @onClose="onModalClose"
                     @updated="refresh"></CancelModal>
    </div>
</template>

<script>
    import Vue from 'vue';
    import { SigningStatus, MimeType } from '@/models/enums';
    import BtnsTemplate from '@/components/grid/StandardBtnsTemplate';
    import CreateModal from '@/components/signatures/create.modal';
    import EditModal from '@/components/signatures/edit.modal';
    import DisplayModal from '@/components/signatures/display.modal';
    import SignModal from '@/components/signatures/sign.modal';
    import CancelModal from '@/components/signatures/cancel.modal';
    import ProgressBar from '@/components/common/ProgressBar.vue';
    import signingService from '@/services/signing.service';
    import signService from '@/services/sign.service';
    import { SignaturePdfData } from '@/models/signaturePdfData.models';
    import { SignatureDocxData } from '@/models/signatureDocxData.models';
    import { mapGetters } from 'vuex';
    import CustomTabs from '@/components/tab/CustomTabs';
    Vue.use(CustomTabs);

    export default {
        components: {
            CreateModal,
            EditModal,
            DisplayModal,
            SignModal,
            CancelModal,
            ProgressBar
        },
        data() {
            return {
                schemaModelFields: {
                    id: { name: 'id', type: 'number', nullable: false },
                    title: { name: 'title', type: 'string', nullable: true },
                    statusId: { name: 'statusId', type: 'string', nullable: true },
                    statusName: { name: 'statusName', type: 'string', nullable: true },
                    statusNameEn: { name: 'statusNameEn', type: 'string', nullable: true },
                    creatorName: { name: 'creatorName', type: 'string', nullable: true },
                    creatorNameEn: { name: 'creatorNameEn', type: 'string', nullable: true },
                    signatoryId: { name: 'signatoryId', type: 'string', nullable: true },
                    creatorId: { name: 'creatorId', type: 'string', nullable: true },
                    signatoryName: { name: 'signatoryName', type: 'string', nullable: true },
                    signatoryNameEn: { name: 'signatoryNameEn', type: 'string', nullable: true },
                    deadline: { name: 'deadline', type: 'date', nullable: true },
                    relatedTaskId: { name: 'relatedTaskId', type: 'number', nullable: true },
                    relatedCoordinationId: { name: 'relatedCoordinationId', type: 'number', nullable: true },
                    isOverdue: { name: 'isOverdue', type: 'boolean', nullable: true },
                    startDate: { name: 'startDate', type: 'date', nullable: true },

                },
                attachmentsSchemaModelFields: {
                    id: { name: 'id', type: 'number', nullable: false },
                    description: { name: 'description', type: 'string', nullable: true },
                    fileName: { name: 'fileName', type: 'string', nullable: true },
                    type: { name: 'type', type: 'string', nullable: true },
                    createdBy: { name: 'createdBy', type: 'string', nullable: true },
                    createdOn: { name: 'createdOn', type: 'date', nullable: true },
                    docTypeCode: { name: 'docTypeCode', type: 'string', nullable: true },
                    signatoryName: { name: 'signatoryName', type: 'string', nullable: true },
                    signingDate: { name: 'signingDate', type: 'date', nullable: true },
                    signingId: { name: 'signingId', type: 'number', nullable: false },
                    signingTitle: { name: 'signingTitle', type: 'string', nullable: true },
                    signingCreatorShortFullName: { name: 'signingCreatorShortFullName', type: 'string', nullable: true },
                    signingCreatorFullName: { name: 'signingCreatorFullName', type: 'string', nullable: true },
                    assigneeId: { name: 'assigneeId', type: 'string', nullable: true },
                    assigneeShortFullName: { name: 'assigneeShortFullName', type: 'string', nullable: true },
                    assigneeFullName: { name: 'assigneeFullName', type: 'string', nullable: true },
                    signingDeadline: { name: 'signingDeadline', type: 'date', nullable: true },
                    signingIsOverdue: { name: 'signingIsOverdue', type: 'boolean', nullable: true },
                    attachmentIsSigned: { name: 'attachmentIsSigned', type: 'boolean', nullable: true },
                    signingStatus: { name: 'signingStatus', type: 'string', nullable: true },
                    signingStatusName: { name: 'signingStatusName', type: 'string', nullable: true },
                    signingStartDate: { name: 'signingStartDate', type: 'date', nullable: true },

                },
                assignedToMeTransportSettings: {
                    read: {
                        url: this.$store.getters.baseUrl + '/api/signing/AssignTo',
                        dataType: 'json',
                        type: 'POST',
                        contentType: 'application/json',
                        beforeSend: this.beforeSend
                    },
                    parameterMap: this.parameterMap
                },
                createdByMeTransportSettings: {
                    read: {
                        url: this.$store.getters.baseUrl + '/api/signing/AssignBy',
                        dataType: 'json',
                        type: 'POST',
                        contentType: 'application/json',
                        beforeSend: this.beforeSend
                    },
                    parameterMap: this.parameterMap
                },
                assignedToMeAttachmentsTransportSettings: {
                    read: {
                        url: this.$store.getters.baseUrl + '/api/signing/AssignedToUserAttachments',
                        dataType: 'json',
                        type: 'POST',
                        contentType: 'application/json',
                        beforeSend: this.beforeSend
                    },
                    parameterMap: this.parameterMap
                },
                showEdit: false,
                showCreate: false,
                showDisplay: false,
                showSign: false,
                showCancel: false,
                activeItem: null,
                activeItemId: null,
                defaultGridRows: this.$store.getters.defaultGridRows,
                attachmentsFilter: 'forSigning',
                signingProceduresFilter: 'started',
                isSigning: false,
                progress: 0,
				progressMax: 0,
				progressText: '',
				selectedCertThumbprint: ''
            };
        },
        computed: {
            ...mapGetters(['claims', 'userId', 'gridOptions', 'authToken', 'dateTimeFormat', 'signingAllowFileTypes', 'appSigningSettings', 'userName', 'userEmail', 'userFullName', 'userFullNameEn']),
            customDateTimeFormat() {
                return `{0: ${this.dateTimeFormat}}`;
            },
            attachmentsGridToolbar() {
                const btns = [];

                if (this.claims.tasks.update) {
                    btns.push({
                        name: "excel",
                        text: this.$t('signing.sign'),
                        iconClass: 'k-icon k-i-pencil'
                    });
                }

                return btns;
            },
        },
        watch: {
            attachmentsFilter: function () {
                this.$refs['assignedToMeAttachmentsGrid'].kendoWidget().dataSource.read();
            },
            signingProceduresFilter: function () {
                this.$refs['assignedToMeGrid'].kendoWidget().dataSource.read();
            },
        },
        mounted() {
            if (this.$route.query['view'] || this.$route.query['answer']) {
                var signingId = this.$route.query['view'] || this.$route.query['answer'];

                if (this.$route.query['view']) {
                    this.showSigning(signingId.trim());
                }
            }
        },
        methods: {
            onDatabound(e) {
                var items = e.sender.items();
                const that = this;
                items.each(function (idx, item) {
                    let dataItem = e.sender.dataItem(item);
                    if (dataItem.signatoryId === that.$store.getters.userId) {
                        if (dataItem.isOverdue) {
                            $(item).addClass('left-border-danger');
                        } else if (dataItem.statusId === SigningStatus.started) {
                            $(item).addClass('left-border-blue');
                        }
                    }
                });
            },
            onAttachmentsDatabound(e) {
                const signingAllowFileTypes = this.signingAllowFileTypes
                var items = e.sender.items();
                const that = this;
                items.each(function (idx, item) {
                    let dataItem = e.sender.dataItem(item);
                    if (dataItem.assigneeId === that.$store.getters.userId && !dataItem.attachmentIsSigned) {
                        if (dataItem.signingIsOverdue) {
                            $(item).addClass('left-border-danger');
                        } else if (dataItem.signingStatus === SigningStatus.started) {
                            $(item).addClass('left-border-blue');
                        }
                    }

                    if (dataItem.signingStatus !== SigningStatus.started || dataItem.attachmentIsSigned || !signingAllowFileTypes.includes(dataItem.type)) {
                        $(item).addClass('checkbox-disabled');
                    }
                });
            },
            onCreatedByMeDatabound(e) {
                var items = e.sender.items();
                const that = this;
                items.each(function (idx, item) {
                    let dataItem = e.sender.dataItem(item);
                    if (dataItem.statusId === SigningStatus.new) {
                        if (dataItem.creatorId === that.$store.getters.userId) {
                            $(item).addClass('left-border-warning');
                        }
                    }
                });
            },
            parameterMap: function (options, operation) {
                if (operation !== 'read' && options.models) {
                    return { models: kendo.stringify(options.models) };
                } else {
                    let newOptions = {
                        request: options,
                        attachmentsFilter: this.attachmentsFilter,
                        signingProceduresFilter: this.signingProceduresFilter
                    };
                    return kendo.stringify(newOptions);
                }
            },
            beforeSend: function (req) {
                if (this.authToken) {
                    req.setRequestHeader('Authorization', 'Bearer ' + this.authToken);
                }
            },
            btnsTemplate: function (e) {
                return {
                    template: Vue.component(BtnsTemplate.name, BtnsTemplate),
                    templateArgs: {
                        ...e,
                        btns: [
                            {
                                name: 'start',
                                text: '',
                                tooltip: this.$t('coordinations.start'),
                                class: 'btn btn-success',
                                icon: 'fas fa-play',
                                show: e.statusId === SigningStatus.new && this.claims.tasks.update,
                                clickHandler: this.onStartClick
                            },
                            {
                                name: 'edit',
                                text: '',
                                tooltip: this.$t('buttons.edit.tooltipText'),
                                class: 'btn btn-success',
                                icon: 'fas fa-edit',
                                show: e.statusId === SigningStatus.new && this.claims.tasks.update,
                                clickHandler: this.showEditModal
                            },
                            {
                                name: 'details',
                                text: '',
                                tooltip: this.$t('buttons.details.tooltipText'),
                                class: 'btn btn-info',
                                icon: 'fas fa-eye',
                                show: this.claims.tasks.read,
                                clickHandler: this.showDisplayModal
                            },
                            {
                                name: 'cancel',
                                text: '',
                                tooltip: this.$t('coordinations.cancel'),
                                class: 'btn btn-info',
                                icon: 'fas fa-ban',
                                show: this.claims.tasks.update && e.statusId === SigningStatus.started,
                                clickHandler: this.onCancel
                            },
                            {
                                name: 'delete',
                                text: '',
                                tooltip: this.$t('buttons.delete.tooltipText'),
                                class: 'btn btn-danger',
                                icon: 'fas fa-trash',
                                show: e.statusId === SigningStatus.new && this.claims.tasks.delete,
                                clickHandler: this.onDelete
                            }
                        ]
                    }
                };
            },
            assignToMeBtnsTemplate: function (e) {
                return {
                    template: Vue.component(BtnsTemplate.name, BtnsTemplate),
                    templateArgs: {
                        ...e,
                        // parentComponent: this.$refs.createdByMeGrid,
                        btns: [
                            {
                                name: 'sign',
                                text: '',
                                tooltip: this.$t('signing.sign'),
                                class: 'btn btn-info',
                                icon: 'fas fa-edit',
                                show:
                                    this.claims.tasks.update &&
                                    e.statusId === SigningStatus.started &&
                                    e.signatoryId === this.$store.getters.userId,
                                clickHandler: this.showSignModal
                            },
                            {
                                name: 'complete',
                                text: '',
                                tooltip: this.$t('signing.complete'),
                                class: 'btn btn-success',
                                icon: 'fas fa-check',
                                show: this.claims.tasks.update &&
                                    e.statusId === SigningStatus.started &&
                                    e.signatoryId === this.$store.getters.userId,
                                clickHandler: this.onCompleteTask
                            },
                            {
                                name: 'details',
                                text: '',
                                tooltip: this.$t('buttons.details.tooltipText'),
                                class: 'btn btn-info',
                                icon: 'fas fa-eye',
                                show: this.claims.tasks.read,
                                clickHandler: this.showDisplayModal
                            }
                        ]
                    }
                };
            },
            assignedToMeAttachmentsBtnsTemplate: function (e) {
                return {
                    template: Vue.component(BtnsTemplate.name, BtnsTemplate),
                    templateArgs: {
                        ...e,
                        // parentComponent: this.$refs.createdByMeGrid,
                        btns: [
                            {
                                name: 'details',
                                text: '',
                                tooltip: this.$t('buttons.details.tooltipText'),
                                class: 'btn btn-info',
                                icon: 'fas fa-eye',
                                show: this.claims.tasks.read,
                                clickHandler: this.showDisplayModalFromAttachment
                            },
                            {
							name: 'download',
                                text: '',
                                tooltip: this.$t('buttons.download.tooltipText'),
                                class: 'btn btn-success',
                                icon: 'fas fa-download',
                                show: this.claims.tasks.read,
                                clickHandler: this.downloadDoc
                            },
                        ]
                    }
                };
            },
            refresh() {
                this.$refs['createdByMeGrid'].kendoWidget().dataSource.read();
                this.$refs['assignedToMeGrid'].kendoWidget().dataSource.read();
                this.$refs['assignedToMeAttachmentsGrid'].kendoWidget().dataSource.read();
            },
            exportCoordination(args) {
                this.$http
                    .get('/api/coordinations/pdfExport/' + args.id, { responseType: 'blob' })
                    .then(response => {
                        const blob = new Blob([response.data], { type: 'application/pdf' });
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', 'Coordination.pdf'); // or any other extension
                        document.body.appendChild(link);
                        link.click();
                    })
                    .catch(error => {
                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: 'danger',
                            time: this.$store.getters.notificationDuration
                        });
                    });
            },
            showEditModal(item) {
                this.activeItem = { ...item };
                this.showEdit = true;
            },
            showDisplayModal(item) {
                this.activeItem = { ...item };
                this.showDisplay = true;
            },
            async showDisplayModalFromAttachment(item) {
                const result = await signingService.getById(item.signingId);
                if (result.data) {
                    this.activeItem = result.data;
                    this.showDisplay = true;
                }
            },
            showSignModal(item) {
                this.activeItem = { ...item };
                this.showSign = true;
            },
            onModalClose() {
                this.activeItemId = null;
                this.activeItem = null;
                this.showCreate = false;
                this.showEdit = false;
                this.showDisplay = false;
                this.showSign = false;
                this.showSigning = false;
            },
            onStartClick(e) {
                this.startId = e.id;
                this.$vs.dialog({
                    type: 'confirm',
                    color: 'success',
                    title: this.$t('signing.startTitle'),
                    text: this.$t('signing.startMessage'),
                    accept: this.startSigning,
                    acceptText: this.$t('buttons.ok'),
                    cancelText: this.$t('buttons.cancel'),
                    cancel: () => (this.startId = null)
                });
            },
            onCompleteTask(e) {
                this.startId = e.id;
                this.$vs.dialog({
                    type: 'confirm',
                    color: 'success',
                    title: this.$t('signing.completeTitle'),
                    text: this.$t('signing.completeMessage'),
                    accept: this.completeSigning,
                    acceptText: this.$t('buttons.ok'),
                    cancelText: this.$t('buttons.cancel'),
                    cancel: () => (this.startId = null)
                });
            },
            startSigning() {
                signingService.start(this.startId)
                    .then(() => {
                        this.$vs.notify({
                            color: 'success',
                            title: this.$t('updateSuccess'),
                            text: ''
                        });
                        this.refresh();
                    })
                    .catch(err =>
                        this.$vs.notify({
                            color: 'danger',
                            title: this.$t('notifications.error'),
                            text: this.$helpers.errorText(err)
                        })
                    )
                    .then(() => (this.startId = null));
            },
            completeSigning() {
                signingService.complete(this.startId)
                    .then(() => {
                        this.$vs.notify({
                            color: 'success',
                            title: this.$t('updateSuccess'),
                            text: ''
                        });
                        this.refresh();
                    })
                    .catch(err =>
                        this.$vs.notify({
                            color: 'danger',
                            title: this.$t('notifications.error'),
                            text: this.$helpers.errorText(err)
                        })
                    )
                    .then(() => (this.startId = null));
            },
            onCancel(e) {
                this.activeItemId = e.id;
                this.showCancel = true;
            },
            onDelete(e) {
                this.deleteId = e.id;
                this.$vs.dialog({
                    type: 'confirm',
                    color: 'danger',
                    title: this.$t('buttons.delete.btnText'),
                    text: this.$t('signing.deletionConfirmMessage'),
                    accept: this.deleteTask,
                    acceptText: this.$t('buttons.ok'),
                    cancelText: this.$t('buttons.cancel'),
                    cancel: () => (this.deleteId = null)
                });
            },
            deleteTask() {
                signingService.delete(this.deleteId)
                    .then(() => {
                        this.$vs.notify({
                            color: 'success',
                            title: this.$t('buttons.delete.btnText'),
                            text: this.$t('deleteSuccess')
                        });
                        this.refresh();
                    })
                    .catch(err =>
                        this.$vs.notify({
                            color: 'danger',
                            title: this.$t('buttons.delete.btnText'),
                            text: this.$t('notifications.error')
                        })
                    )
                    .then(() => (this.deleteId = null));
            },
            showSigning(signingId) {
                this.$http
                    .get('/api/signing/get/' + signingId)
                    .then(response => {
                        this.showDisplayModal(response.data);
                    })
                    .catch(err => {
                        this.$vs.notify({
                            color: 'danger',
                            title: this.$t('notifications.error'),
                            text: this.$helpers.errorText(err)
                        });
                    });
            },
            isSignedCellTemplate(dataItem) {
                let templateHtml =
                    '<span class="badge ' + (dataItem.attachmentIsSigned ? 'badge-success' : 'badge-light') + '">' + kendo.htmlEncode(dataItem.attachmentIsSigned ? this.$t('yes') : this.$t('no')) + '</span>';

                return templateHtml;
            },
            downloadDoc(e) {
                const downloadUri = this.$store.getters.baseUrl + `/api/file/download/?id=${e.id}&type=${e.docTypeCode}&hash=${e.hash}`;
                this.loading = true;
                this.$http.get(downloadUri, { responseType: 'blob' })
                    .then(response => {
                        const blob = new Blob([response.data]);
                        const url = window.URL.createObjectURL(blob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', e.fileName); // or any other extension
                        document.body.appendChild(link);
                        link.click();
                        window.URL.revokeObjectURL(url);
                    })
                    .catch(err => {
                        this.$vs.notify({
                            color: 'danger',
                            title: this.$t('notifications.error'),
                            text: this.$helpers.errorText(err)
                        })
                    })
                    .then(() => { this.loading = false; });
            },
            beforeExcelExport(e) {
                e.preventDefault();

                this.onSignClick();
            },
            async onSignClick() {
                this.resetProgressBar();

                const signingAllowFileTypes = this.signingAllowFileTypes;
                const selectedAttachments = this.getGridSelectedRows()
                    .filter(x => !x.attachmentIsSigned && signingAllowFileTypes.includes(x.type));

                if (!selectedAttachments.length) {
                    return this.$vs.notify({
                        title: this.$t('signing.signing'),
                        text: this.$t('signing.missingSelectedAttachmentsToSign'),
                        color: 'warning',
                        time: this.$store.getters.notificationDuration,
                    });
                }

                if (!this.appSigningSettings) {
                    console.log('AppSigningSettings not loaded. Dispatch loadAppSigningSettingsAsync');
                    // Ако по някаква причина не са заредени конф.настройки за подписването се опитваме да ги заредим.
                    await this.$store.dispatch('loadAppSigningSettingsAsync');
                }

                const count = selectedAttachments.length;
                this.progressMax = count + 1;

                this.isSigning = true;

                try {
                    const signatureBase64Image = (await this.downloadUserSignatureImage()).data;
                    for (const attachment of selectedAttachments) {
                        this.progress += 1;
                        try {
                            await this.signAttachment(attachment, signatureBase64Image);
                        } catch (error) {
                            console.log(attachment, error);
                        }
                    }
                } catch (error) {
                    console.log(error);
                }

                this.refresh();
                this.isSigning = false;
                this.resetProgressBar();
            },
            async signAttachment(attachment, signatureBase64Image) {
                this.progressText = `Сваляне на ${attachment.fileName}`;
                const attachmentBase64Content = (await signService.getBase64OfAFile(attachment)).data;

                this.progressText = `Подписване на ${attachment.fileName}`;

                let signResult;
                switch (attachment.type) {
                    case MimeType.Pdf:
                        signResult = await this.signPdf(attachmentBase64Content, signatureBase64Image);
                        break;
                    case MimeType.Docx:
                        signResult = await this.signDocx(attachmentBase64Content);
                        break;
                    default:
                        break;
                }

                await this.saveSigningResult(attachment, signResult);
			},
            async signPdf(attachmentBase64Content, signatureBase64Image) {
                const payload = new SignaturePdfData({
                    contents: attachmentBase64Content,
                    thumbprint: this.selectedCertThumbprint
                });

                // payload.options.contact = this.userFullNameEn; // Contact Не се показва във визуалзацията на подписа
                // payload.options.reason = this.userFullNameEn; // Кирилица не се визуализира (ограничение в шрифтовете на iTextSharp)

                if (signatureBase64Image) {
                    payload.options.signatureImageBase64 = signatureBase64Image;
                }


                if (this.appSigningSettings && this.appSigningSettings.usersSettings) {
                    const userSigningSettings = this.appSigningSettings.usersSettings.find(x => x.username === this.userName);
                    console.log('userSigningSettings', userSigningSettings);

                    if (userSigningSettings && userSigningSettings.bookmarkId) {
                        payload.options.visualizatonType = 'annotation';
                        payload.options.bookmarkName = userSigningSettings.bookmarkId
                        payload.options.xStep = this.appSigningSettings.defaultPdfXStep;
                        payload.options.yStep = this.appSigningSettings.defaultPdfYStep;
                    }
                }

                return (await signService.signPdf(payload)).data;
            },
            async signDocx(attachmentBase64Content) {
                const payload = new SignatureDocxData({
                    contents: attachmentBase64Content,
                    thumbprint: this.selectedCertThumbprint,
                    options: {
                        name: this.userFullName,
                        email: this.userEmail
                    }
                });

                return (await signService.signDocx(payload)).data;
            },
            async saveSigningResult(attachment, signResult) {
                if (signResult && signResult.isError === false) {
                    // Ще поиска сертификат само първия път. В другите случаи подаваме thumbprint-а на избрания сертификат.
                    this.selectedCertThumbprint = signResult.thumbprint;
                    this.progressText = `Запазване на подписан ${attachment.fileName}`;
                    const saveResult = (await signingService.saveSignedAttachment({
                        signingId: attachment.signingId,
                        attachmentId: attachment.id,
                        fileName: attachment.fileName,
                        docTypeCode: attachment.docTypeCode,
                        signedBase64Content: signResult.contents
                    })).data;

                    if (saveResult && saveResult.isSigned === true) {
                        attachment.isSigned = saveResult.isSigned;
                        attachment.signatoryName = saveResult.signatoryName;
                        attachment.signingDate = saveResult.signingDate;
                    }

                    console.log(saveResult);
                } else {
                    throw Error(JSON.stringify(signResult));
                }
            },
            async downloadUserSignatureImage() {
                const downloadUri = this.$store.getters.baseUrl + `/api/user/SignatureImageBase64`;
                const response = await this.$http.get(downloadUri);
                return response;
            },
            getGridSelectedRows() {
                const grid = this.$refs['assignedToMeAttachmentsGrid'];
                if (!grid) return null;
                const selectedItems = this.$helpers.getGridSelectedRows(grid.kendoWidget());
                return selectedItems;
            },
            resetProgressBar() {
                this.progressMax = 0;
                this.progress = 0;
                this.progressText = '';
                this.selectedCertThumbprint = '';
            }
        }
    };
</script>

<style lang="scss">
    tr.checkbox-disabled  input.k-checkbox {
        display: none !important;
    }

    tr.checkbox-disabled  label.k-checkbox-label {
        display: none !important;
    }

    tr.left-border-danger {
        &:hover {
            font-weight: bold;
            font-size: 1.3em;
            color: rgba(255, 0, 0, 0.7) !important;
        }

        td:first-child {
            border-left: 7px solid rgba(255, 0, 0, 0.5) !important;
        }
    }

    tr.left-border-warning {
        &:hover {
            font-weight: bold;
            font-size: 1.3em;
            color: rgba(255, 127, 7, 0.7) !important;
        }

        td:first-child {
            border-left: 7px solid rgba(255, 127, 7, 0.5) !important;
        }
    }

    tr.left-border-blue {
        &:hover {
            font-weight: bold;
            font-size: 1.3em;
            color: #3498db !important;
        }

        td:first-child {
            border-left: 7px solid #3498db !important;
        }
    }

    tr.left-border-green {
        &:hover {
            font-weight: bold;
            font-size: 1.3em;
            color: #4caf50 !important;
        }

        td:first-child {
            border-left: 7px solid #4caf50 !important;
        }
    }
</style>
